// SSO buttons. Google is wired to the backend OAuth flow
// (GET /auth/google/login → Google consent → /auth/callback). GitHub is not
// implemented yet, so it's disabled rather than silently doing nothing.
import { useEffect, useState } from "react";
import { getApiBase } from "@/api/client";
import { getAuthProviders } from "@/api/auth";

export default function SocialButtons({ next = null }) {
  // Ask the backend whether Google is actually configured + switched on. A
  // SuperAdmin can turn it off (or never set credentials), and offering a button
  // that 503s is worse than not offering one. Null = still checking.
  const [googleOn, setGoogleOn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getAuthProviders()
      .then((p) => { if (!cancelled) setGoogleOn(!!p.google); })
      // If the probe itself fails (backend down), show the button rather than
      // hide a working login behind a transient network error.
      .catch(() => { if (!cancelled) setGoogleOn(true); });
    return () => { cancelled = true; };
  }, []);

  // Full-page navigate to the backend so it can 302 us to Google's consent
  // screen. In dev this hits the /api proxy; in prod it hits the absolute API
  // base. `next` (an in-app path) round-trips back to us after login.
  const startGoogle = () => {
    const q = next ? `?next=${encodeURIComponent(next)}` : "";
    window.location.href = `${getApiBase()}/auth/google/login${q}`;
  };

  // Google off → GitHub (disabled, coming soon) is the only tile left, so the
  // whole row is noise. Hide it entirely and let the email form stand alone.
  if (googleOn === false) return null;

  return (
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={startGoogle}
        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.86c2.26-2.08 3.56-5.15 3.56-8.87z"
          />
          <path
            fill="#34A853"
            d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A12 12 0 0 0 12 24z"
          />
          <path
            fill="#FBBC05"
            d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"
          />
          <path
            fill="#EA4335"
            d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
          />
        </svg>
        Google
      </button>
      <button
        type="button"
        disabled
        title="GitHub sign-in is coming soon"
        className="inline-flex h-11 cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white text-sm font-medium text-slate-400 opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-500"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 .5C5.37.5 0 5.87 0 12.5c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58v-2.03c-3.34.73-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5.99.11-.78.42-1.3.76-1.6-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.12-.31-.54-1.52.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.28-1.55 3.29-1.23 3.29-1.23.66 1.66.24 2.87.12 3.18.77.84 1.23 1.91 1.23 3.22 0 4.61-2.8 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 24 12.5C24 5.87 18.63.5 12 .5z" />
        </svg>
        GitHub
      </button>
    </div>
  );
}
